import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import { TourTooltip } from './TourTooltip';
import { tourStyles } from './tourStyles';
import { dashboardTour } from './tours/dashboardTour';
import { draftEditorTour } from './tours/draftEditorTour';
import { revisionsTour } from './tours/revisionsTour';
import type {
  TourCallbackData,
  TourConfig,
  TourContextValue,
  TourName,
  TourState,
} from './types';
import { TOUR_STORAGE_KEY } from './types';

const tours: Record<TourName, TourConfig> = {
  dashboard: dashboardTour,
  revisions: revisionsTour,
  draftEditor: draftEditorTour,
};

const getCompletedTours = (): TourName[] => {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveCompletedTours = (completedTours: TourName[]) => {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedTours));
  } catch {
    // Ignore storage errors
  }
};

const initialState: TourState = {
  activeTour: null,
  stepIndex: 0,
  isRunning: false,
  completedTours: getCompletedTours(),
};

export const TourContext = createContext<TourContextValue | null>(null);

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider = ({ children }: TourProviderProps) => {
  const [state, setState] = useState<TourState>(initialState);

  // Load completed tours from localStorage on mount
  useEffect(() => {
    const completedTours = getCompletedTours();
    setState((prev) => ({ ...prev, completedTours }));
  }, []);

  const startTour = useCallback((tourName: TourName) => {
    setState((prev) => ({
      ...prev,
      activeTour: tourName,
      stepIndex: 0,
      isRunning: true,
    }));
  }, []);

  const endTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeTour: null,
      stepIndex: 0,
      isRunning: false,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      stepIndex: prev.stepIndex + 1,
    }));
  }, []);

  const prevStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      stepIndex: Math.max(0, prev.stepIndex - 1),
    }));
  }, []);

  const goToStep = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      stepIndex: index,
    }));
  }, []);

  const markTourCompleted = useCallback((tourName: TourName) => {
    setState((prev) => {
      if (prev.completedTours.includes(tourName)) {
        return prev;
      }
      const newCompletedTours = [...prev.completedTours, tourName];
      saveCompletedTours(newCompletedTours);
      return {
        ...prev,
        completedTours: newCompletedTours,
      };
    });
  }, []);

  const resetTour = useCallback((tourName: TourName) => {
    setState((prev) => {
      const newCompletedTours = prev.completedTours.filter(
        (t) => t !== tourName
      );
      saveCompletedTours(newCompletedTours);
      return {
        ...prev,
        completedTours: newCompletedTours,
      };
    });
  }, []);

  const isTourCompleted = useCallback(
    (tourName: TourName) => {
      return state.completedTours.includes(tourName);
    },
    [state.completedTours]
  );

  const handleJoyrideCallback = useCallback(
    (data: TourCallbackData) => {
      const { action, status, type, index } = data;
      const currentTour = state.activeTour ? tours[state.activeTour] : null;

      // Handle step changes - use index from callback for controlled mode
      if (type === EVENTS.STEP_AFTER) {
        if (action === ACTIONS.NEXT) {
          // Set to next step index directly
          goToStep(index + 1);
        } else if (action === ACTIONS.PREV) {
          // Set to previous step index directly
          goToStep(index - 1);
        }
      }

      // Handle tour end - mark as completed for finish, skip, or close
      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        // Mark tour completed for all end actions (finish, skip, close)
        // This prevents auto-restart from useAutoStartTour
        if (state.activeTour) {
          markTourCompleted(state.activeTour);
        }
        endTour();
      }

      // Handle target not found - skip to next step or end tour
      if (type === EVENTS.TARGET_NOT_FOUND) {
        if (currentTour && index < currentTour.steps.length - 1) {
          goToStep(index + 1);
        } else {
          endTour();
        }
      }
    },
    [state.activeTour, goToStep, markTourCompleted, endTour]
  );

  const currentTour = state.activeTour ? tours[state.activeTour] : null;

  const contextValue = useMemo<TourContextValue>(
    () => ({
      state,
      startTour,
      endTour,
      nextStep,
      prevStep,
      goToStep,
      resetTour,
      isTourCompleted,
      markTourCompleted,
    }),
    [
      state,
      startTour,
      endTour,
      nextStep,
      prevStep,
      goToStep,
      resetTour,
      isTourCompleted,
      markTourCompleted,
    ]
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      <Joyride
        steps={currentTour?.steps || []}
        stepIndex={state.stepIndex}
        run={state.isRunning}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        scrollToFirstStep
        spotlightClicks
        styles={tourStyles}
        tooltipComponent={TourTooltip}
        callback={handleJoyrideCallback}
        floaterProps={{
          disableAnimation: false,
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip',
        }}
      />
    </TourContext.Provider>
  );
};
